import './DataIngestion.scss'
import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../stores";
import {Alert, Button, ButtonGroup, Chip, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import {routes} from '../../routing/routes';
import {generatePath, useNavigate} from "react-router";
import {KoiFacade} from "../../components/koi-facade/KoiFacade";
import {ChevronRight, Delete, OpenInNew, Refresh} from '@mui/icons-material';
import {LoadingSpinnerPie} from "../../components/loading-pie/LoadingSpinnerPie";
import type {DataFileSerializer} from "../../services/classes/IngestionClasses";
import {GenericModalVertical} from "../../components/modal/GenericModalVertical";
import moment from "moment";


export const DatasetOverviewPage: React.FC = observer(() => {
    const {dataIngestionStore, p} = useStores();
    const loading = dataIngestionStore.datasetList === undefined;
    const noData = dataIngestionStore.datasetList?.length === 0;
    const manyData = dataIngestionStore.datasetList && dataIngestionStore.datasetList.length > 9;
    const error = dataIngestionStore.datasetListManager.error;

    const [deleteDialogSubject, setDeleteDialogSubject] = React.useState<DataFileSerializer | undefined>(undefined);
    const [deleteIsLoading, setDeleteIsLoading] = React.useState(false);

    const navigate = useNavigate();
    useEffect(() => {
        dataIngestionStore.datasetListManager.request()
    }, [dataIngestionStore, p])

    const uploadButton = <Button
        onClick={() => navigate(generatePath(routes.data_upload))}
        color="primary"
        variant="outlined"
        endIcon={<ChevronRight/>}
    >
        Upload new dataset
    </Button>;

    const actions = <Grid container className="dataset-overview-actions" justifyContent="center">
        <Grid>
            {uploadButton}
        </Grid>
    </Grid>

    const table = <>
        <Grid container justifyContent="center" sx={{mt: 10, mb: 4}}>
            <Grid size={6}>
                <Typography variant="h6">Name</Typography>
            </Grid>
            <Grid size={2}>
                <Typography variant="h6">Status</Typography>
            </Grid>
            <Grid size={2}>
                <Typography variant="h6">Date</Typography>
            </Grid>
            <Grid size={2}/>
        </Grid>

        {dataIngestionStore.datasetList?.map((dataset) => (
            <Grid key={'dataset' + dataset.id} container justifyContent="center" sx={{mb: 2}}>
                <Grid size={6}>
                    <Typography variant="body1">{dataset.name}</Typography>
                </Grid>
                <Grid size={2}>
                    <Typography variant="body1">
                        {dataset.databag_exists ?
                            <Chip label={`Uploaded${dataset.target_databag_id ? ' [' + dataset.target_databag_id + ']' : ''}`} color="success"
                                  className="dataset-uploaded"/> :
                            <Chip label={`Pending${dataset.target_databag_id ? ' [' + dataset.target_databag_id + ']' : ''}`} color="primary"
                                  className="dataset-pending"/>}
                        {dataset.gcp_job_execution_id && dataset.gcp_job_execution_url &&
                                <Tooltip title={'GCP execution id: ' + dataset.gcp_job_execution_id}>
                                    <span>
                                        <IconButton href={dataset.gcp_job_execution_url} target="_blank"
                                                    rel="noopener noreferrer" size="small">
                                            <OpenInNew/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                        }
                    </Typography>
                </Grid>
                <Grid size={2}>
                    <Typography variant="body1">{moment(dataset.created).format('YYYY-MM-DD')}</Typography>
                </Grid>
                <Grid style={{textAlign: 'right'}} size={2}>
                    <ButtonGroup variant="outlined" color="primary">
                        {!dataset.databag_exists &&
                            <Button color="error" onClick={() => setDeleteDialogSubject(dataset)}>
                                <Delete/>
                            </Button>
                        }
                        <Button
                            onClick={() => navigate(generatePath(routes.data_upload_mapping, {dataFileId: String(dataset.id)}))}>
                            <ChevronRight/>
                        </Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
        ))}
    </>

    const deleteDialog = <GenericModalVertical
        className="dataset-modal"
        title={`Are you sure?`}
        isOpen={!!deleteDialogSubject}
        onSave={() => {
            if (!deleteDialogSubject) return;
            setDeleteIsLoading(true);
            dataIngestionStore.deleteIngestionFile(deleteDialogSubject.id)
                .then(() => setDeleteDialogSubject(undefined))
                .finally(() => setDeleteIsLoading(false))
        }}
        isLoading={deleteIsLoading}
        saveTitle="Delete"
        isDanger
        onCancel={() => setDeleteDialogSubject(undefined)}
        onClose={() => setDeleteDialogSubject(undefined)}
    >
        <Grid container spacing={2} alignContent="center" alignItems="center" direction="row" size={12}>
            <Grid size={12}>
                <Typography style={{marginTop: '1em'}}>
                    Are you sure you want to remove the following dataset?
                </Typography>
                <Typography style={{marginTop: '1em'}} className="filename">
                    {deleteDialogSubject?.name}
                </Typography>
            </Grid>
        </Grid>
    </GenericModalVertical>

    return (
        <KoiFacade
            title="Data Ingestion"
            onBack={() => navigate(routes.home)}
            className={`data-ingestion data-overview-page${noData ? ' no-data' : ''}`}
            next={manyData ? {type: 'component', node: uploadButton} : undefined}>
            {deleteDialog}
            <Grid container justifyContent="center" className={error ? 'error-alert-container' : ''}>
                {error
                    ? <Grid>
                        <Alert
                            severity="error"
                            action={<IconButton
                                size="small"
                                onClick={() => dataIngestionStore.datasetListManager.request()}
                                disabled={dataIngestionStore.datasetListManager.busy}>
                                <Refresh/>
                            </IconButton>
                            }>
                            {error}
                        </Alert>
                    </Grid>
                    : loading
                        ? <Grid><LoadingSpinnerPie/></Grid>
                        : noData
                            ? actions
                            : <Grid size={8}>
                                {table}
                                {actions}
                            </Grid>
                }
            </Grid>
        </KoiFacade>
    );
});
